/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/naming-convention */
import { Box, Button, Flex, FormControl, FormLabel, Input, Select, useToast } from "@chakra-ui/react"
import { InfoOutlineIcon } from "@chakra-ui/icons"
import React, { useEffect, useState } from "react"
import { Colors } from "../../../theme/colors"
import { type ProductFilterInput, useGetProductFilterCriteriaMasterLazyQuery } from "../../../type-and-hooks"
import { Loading } from "../../../components/commons/loader.main"
import { specialTypeSwap } from "./productFilter.support"
import { useAtom } from "jotai"
import { productFilterFormDataAtom } from "./productFilter.support.store"
import { extractValidObjects } from "../../../components/commons/utilsTs"
import { SnapPopoverNotes } from "../../../components/commons/popover"

const FITTING_ALLOWED_INFO_TEXT = [
  "**Yes** - a fitting is approved to be used with this system - also approved for no fitting.",
  "**No** - fitting or butt weld are not approved to be used with this system.",
  "**Butt Weld** - butt weld is approved to be used with this system - also approved for no butt weld."
]

const ELEMENT_SIZE_FORMAT_INFO_TEXT =
  "Select the thickness or format that is equal to or less than your element. For example, for a slab that is 140mm thick select the 120mm thickness not the 150mm thickness."

const FRL_INFO_TEXT =
  "Select the FRL called for in the building design / drawings. This FRL may in some instances be less than the Element's FRL."

export interface ProductDataInterface {
  product_generic_code: string
  product_id: string
}

export interface Option {
  label: string
  value: number | string
}

type ProductFilterInputName =
  | keyof ProductFilterInput
  | "frl"
  | "element_type"
  | "element_construction"
  | "element_size_form"
  | "service"
  | "service_size"
  | "fitting_allowed"
  | "industry_id"
  | "product_type_id"
  | "installation_type_id"
  | "product_code"
export const FMPFilterForm: React.FC = (): React.ReactNode => {
  interface FormInputDataInterface {
    fields?: Array<{
      type: string
      label: string
      placeholder: string
      name: ProductFilterInputName
      isRequired?: boolean
      options?: Option[] | null
      sortType?: "alphabetical" | "numerical" | "custom"
    }>
  }

  // Generic sorting function
  const sortOptions = (options: Option[], sortType?: string): Option[] => {
    if (!options) return []

    switch (sortType) {
    case "numerical":
      return [...options].sort((a, b) => {
        const aNum = parseFloat(a.label.replace(/[^\d.]/g, ""))
        const bNum = parseFloat(b.label.replace(/[^\d.]/g, ""))

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum
        }
        return a.label.localeCompare(b.label)
      })

    case "custom":
      // Add custom sorting logic here if needed
      return [...options].sort((a, b) => a.label.localeCompare(b.label))

    case "alphabetical":
    default:
      return [...options].sort((a, b) => a.label.localeCompare(b.label))
    }
  }
  // Queries
  const [fetchMasterData, { data, loading, error }] = useGetProductFilterCriteriaMasterLazyQuery()

  const [formInputData, setFormInputData] = useState<FormInputDataInterface>()
  const [productFilterFormData, setProductFilterFormData] = useAtom(productFilterFormDataAtom)

  const toast = useToast()
  // Fetching form Data
  const {
    getProductFilterCriteriaMaster: {
      element_types,
      element_display_1,
      element_display_2,
      product_types,
      product_codes,
      service_types,
      service_names,
      service_size,
      industry_categories,
      frl,
      installation_types
    }
  } = data ?? {
    getProductFilterCriteriaMaster: {
      element_types: [],
      element_display_1: [],
      element_display_2: [],
      product_types: [],
      product_codes: [],
      service_types: [],
      service_names: [],
      service_size: [],
      industry_categories: [],
      frl: [],
      fitting: [],
      installation_types: []
    }
  }

  const handleOnchange = <T extends HTMLInputElement | HTMLSelectElement>({ currentTarget }: React.ChangeEvent<T>): void => {
    const { name, value } = currentTarget
    const integerValues = [
      "installation_method_id",
      "product_id",
      "protection_type_id",
      "industry_id",
      "element_thickness",
      "frl_ins",
      "frl_int",
      "frl_st",
      "application_id"
    ]
    setProductFilterFormData({ ...productFilterFormData, [name]: integerValues.includes(name) ? parseInt(value) : value })
  }
  const clearFilters = (): void => {
    const clearedObject = Object.keys(productFilterFormData).reduce((reducer, key) => ({ ...reducer, [key]: "" }), {})
    setProductFilterFormData(clearedObject)
  }

  // Form DataAPI call
  const formChangeHandler = (): void => {
    const formValues = productFilterFormData
    const nullExcludedFormValues = extractValidObjects(formValues, true)
    const keys = Object.keys(nullExcludedFormValues) as Array<keyof ProductFilterInput>
    const specialTypes: ProductFilterInput = keys.reduce((accumulator, key) => {
      return {
        ...accumulator,
        [key]: specialTypeSwap<unknown>(nullExcludedFormValues[key] ?? "")
      }
    }, {})

    if (specialTypes) {
      void fetchMasterData({
        variables: {
          criteria: {
            ...specialTypes
          }
        }
      })
    }
  }

  useEffect(() => {
    if (data) {
      const formData: FormInputDataInterface = {
        fields: [
          {
            type: "select",
            label: "Element Type",
            placeholder: "Element Type",
            name: "element_type_id",
            isRequired: false,
            options: element_types
          },

          {
            type: "select",
            label: "Element Construction",
            placeholder: "Element Construction",
            isRequired: false,
            name: "element_display_1",
            options: sortOptions(element_display_1, "alphabetical")
          },
          {
            type: "select",
            label: "Element Size/Format",
            placeholder: "Element Size/Format",
            isRequired: false,
            name: "element_display_2",
            options: sortOptions(element_display_2, "numerical")
          },
          {
            type: "select",
            label: "Service Type",
            placeholder: "Service Type",
            name: "service_type_id",
            options: sortOptions(service_types, "alphabetical")
          },
          {
            type: "select",
            label: "Service",
            placeholder: "Service",
            name: "service_sub_cat_id",
            options: service_names
          },
          {
            type: "select",
            label: "Service Size",
            placeholder: "Service Size",
            name: "service_size",
            options: sortOptions(service_size, "numerical")
          },
          {
            type: "select",
            label: "Fitting Allowed",
            placeholder: "Fitting Allowed",
            name: "fitting_allowed",
            options: [
              { label: "Yes", value: "Yes" },
              { label: "Butt Weld", value: "Butt Weld" }
            ]
          },
          {
            type: "select",
            label: "Industry",
            placeholder: "Industry",
            name: "industry_category_id",
            options: sortOptions(industry_categories, "alphabetical")
          },
          {
            type: "select",
            label: "Product Type",
            placeholder: "Product Type",
            name: "product_type_id",
            options: product_types
          },
          {
            type: "select",
            label: "Installation Type",
            placeholder: "Installation Type",
            name: "installation_type_id",
            options: sortOptions(installation_types, "alphabetical")
          },
          {
            type: "select",
            label: "Product Code",
            placeholder: "Product Code",
            name: "product_generic_code",
            options: sortOptions(product_codes, "alphabetical")
          },
          {
            type: "select",
            label: "FRL",
            placeholder: "FRL",
            name: "frl",
            options: sortOptions(frl, "numerical")
          }
        ]
      }
      setFormInputData(formData)
    }
  }, [data])

  // type serviceSizeInterface = Record<string, object>

  // const debouncedFormData = useDebounce<Partial<ProductFilterInput>>({ value: productFilterFormData, debounceTime: 50 })
  useEffect(() => {
    formChangeHandler()
  }, [productFilterFormData])

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load. Pls try again - ERR-FMP-01",
        status: "error",
        isClosable: true
      })
    }
  }, [error])

  // fetch master data onload.
  useEffect(() => {
    void fetchMasterData()
  }, [])
  const getGridColumns = (name?: ProductFilterInputName): string => {
    const span2 = ["element_display_1", "service_sub_cat_id", "installation_type_id"]
    if (!name) {
      return "1fr"
    }
    if (name === "element_display_2") {
      return "span 3"
    }
    if (span2.includes(name)) {
      return "span 2"
    }

    return "1fr"
  }

  return (
    <Box bg={loading ? "#f3f3f3" : "transparent"} transition="900" minH="200px" mb="4" position="relative" borderRadius="8px" overflow={"visible"}>
      <Box position="absolute" top="0" w="100%" height="200px" display="flex" alignItems="center" justifyContent="center">
        <Loading loading={loading} />
      </Box>
      <form>
        <Box
          display={"grid"}
          gridTemplateColumns={["1fr", "1fr", "1fr", "repeat(6, auto)"]}
          gridTemplateRows={"1fr 1fr auto"}
          gridGap={"8px"}
          alignItems={"flex-end"}
          opacity={loading ? 0.5 : 1}
        >
          {formInputData?.fields?.map(({ label, type, name, placeholder, isRequired, options }, index) => (
            <FormControl key={index} gridColumn={["1fr", "1fr", "1fr", getGridColumns(name)]}>
              <Flex alignItems="center" gap="4px" mb="3px">
                <FormLabel color={Colors.Primary.accent} fontSize={"14px"} fontWeight={"700"} mb="0">
                  {isRequired && "*"}
                  {label}
                </FormLabel>
                {name === "fitting_allowed" && (
                  <SnapPopoverNotes text={FITTING_ALLOWED_INFO_TEXT} noteIcon={<InfoOutlineIcon />} placement="auto" usePortal />
                )}
                {name === "element_display_2" && (
                  <SnapPopoverNotes text={ELEMENT_SIZE_FORMAT_INFO_TEXT} noteIcon={<InfoOutlineIcon />} placement="auto" usePortal />
                )}
                {name === "frl" && (
                  <SnapPopoverNotes text={FRL_INFO_TEXT} noteIcon={<InfoOutlineIcon />} placement="auto" usePortal />
                )}
              </Flex>
              {type === "text" && (
                <Input
                  type="number"
                  value={productFilterFormData[name]?.toString()}
                  name={name}
                  placeholder={placeholder}
                  onChange={handleOnchange}
                  minW={"100px"}
                />
              )}
              {type === "select" && (
                <Select
                  value={productFilterFormData[name]?.toString()}
                  borderRadius={"2px"}
                  placeholder={productFilterFormData[name] ? "Reset" : "Select"}
                  color={productFilterFormData[name] ? Colors.Primary.black : Colors.SecondaryPrimary.c2}
                  name={name}
                  bg={Colors.Secondary.c2_100}
                  borderColor={Colors.Secondary.c2_800}
                  onChange={handleOnchange}
                  minW={"100px"}
                >
                  {(options ?? []).map(({ label, value }, index) => (
                    <option key={`${label}_${value}_${index}`} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>
          ))}
          <Box gridColumn={["1fr", "1fr", "1fr", getGridColumns()]}>
            <Button
              display="flex"
              alignItems="center"
              gridColumnStart="6"
              height="40px"
              variant="Button1RegularAccent"
              _hover={{ bg: "#8B6B5E" }}
              textAlign="center"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  )
}
